import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge } from '~/components/ui/Badge';
import { createTablePage } from '~/components/ui/createTablePage';
import { LIST_PAGE_SIZE } from '~/components/ui/Pagination';

type HelperApplication = components['schemas']['HelperApplicationSummary'];
type HelperApplicationRole = components['schemas']['HelperApplicationRole'];

// The queue now holds three intake forms, not one: GF 4 (study helper), GF 12
// (follow-up coordinator) and GF 14 (follow-up partner). Spellings follow the
// two labels `web` already ships for the same polymorphic identities
// (`web/public/locales/en/translation.json:286,1134`); `admin` has no i18n, so
// they are literals here.
const roleLabels: Record<HelperApplicationRole, string> = {
  STUDY_HELPER: 'Study Helper',
  FOLLOWUP_COORDINATOR: 'Follow-up Coordinator',
  FOLLOWUP_PARTNER: 'Follow-up Partner',
};

const columnHelper = createColumnHelper<HelperApplication>();

const columns = [
  columnHelper.accessor('id', {
    header: 'ID',
  }),
  columnHelper.accessor('role', {
    header: 'Role',
    cell: (info) => roleLabels[info.getValue()],
  }),
  columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
    id: 'name',
    header: 'Name',
  }),
  columnHelper.accessor('email', {
    header: 'Email',
  }),
  columnHelper.accessor('city', {
    header: 'City',
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('country', {
    header: 'Country',
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('congregation_name', {
    header: 'Congregation',
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('created_at', {
    header: 'Created',
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
  }),
  columnHelper.accessor('agreements_accepted', {
    header: 'Agreements',
    cell: (info) => (
      <Badge $variant={info.getValue() ? 'success' : 'neutral'}>
        {info.getValue() ? 'Yes' : 'No'}
      </Badge>
    ),
  }),
];

const ApplicationsList = createTablePage<
  HelperApplication,
  '/applications/$applicationId'
>({
  name: 'Applications',
  useData: (page: number) => {
    return api.listHelperApplications.useQuery({
      query: {
        limit: LIST_PAGE_SIZE,
        offset: LIST_PAGE_SIZE * page,
      },
    });
  },
  columns: columns,
  itemBaseUrl: '/applications/$applicationId',
  getParams: (row) => ({ applicationId: String(row.id) }),
});

export const Route = createFileRoute('/_admin/applications/')({
  component: ApplicationsList,
});
